import { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, CheckCheck, MessageSquare, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { NotificationDto } from '@/api/notifications.api';
import { useAuth } from '@/auth/AuthProvider';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotificationUnreadCount, useNotifications } from '@/hooks/useNotifications';

const dateText = (value: string) => new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const unread = useNotificationUnreadCount(Boolean(user));
  const list = useNotifications({ page: 1, pageSize: 20, channel: 'IN_APP' }, open && Boolean(user));
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const notifications = useMemo(() => [...(list.data?.items ?? [])].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)), [list.data?.items]);
  const unreadCount = unread.data?.unreadCount ?? 0;

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (event: MouseEvent) => { if (!rootRef.current?.contains(event.target as Node)) setOpen(false); };
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', closeOnOutside);
    window.addEventListener('keydown', closeOnEscape);
    return () => { document.removeEventListener('mousedown', closeOnOutside); window.removeEventListener('keydown', closeOnEscape); };
  }, [open]);

  const openNotification = async (notification: NotificationDto) => {
    setError('');
    try {
      await markRead.mutateAsync(notification.id);
      setOpen(false);
      if (notification.relatedEntityType === 'PURCHASE_ORDER' && notification.relatedEntityId) {
        navigate(`${user?.userType === 'VENDOR' ? '/portal/purchase-orders' : '/app/purchase-orders'}/${notification.relatedEntityId}#conversation`);
      } else if (user?.userType !== 'VENDOR') navigate('/app/notifications');
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to open notification.'); }
  };

  const readAll = async () => {
    setError('');
    try { await markAll.mutateAsync(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to mark notifications as read.'); }
  };

  return <div className="notification-bell" ref={rootRef}>
    <button className="icon-button notification-bell__trigger" aria-label={`${unreadCount} unread notifications`} aria-expanded={open} onClick={() => setOpen((value) => !value)}><Bell size={19} />{unreadCount > 0 && <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}</button>
    {open && <section className="notification-popover" aria-label="Notifications"><header><div><strong>Notifications</strong><span>{unreadCount ? `${unreadCount} unread` : 'You are all caught up'}</span></div><button className="icon-button" aria-label="Close notifications" onClick={() => setOpen(false)}><X /></button></header>
      {unreadCount > 0 && <button className="notification-read-all" disabled={markAll.isPending} onClick={() => void readAll()}><CheckCheck /> {markAll.isPending ? 'Marking as read…' : 'Mark all as read'}</button>}
      {error && <div className="notification-popover__error">{error}</div>}
      <div className="notification-popover__list">{list.isLoading ? <div className="notification-popover__state">Loading notifications…</div> : list.isError ? <div className="notification-popover__state">Notifications could not be loaded.</div> : notifications.length === 0 ? <div className="notification-popover__state"><Bell /><strong>No notifications</strong><span>New alerts will appear here.</span></div> : notifications.map((notification) => <button className={`notification-item${notification.isRead ? '' : ' is-unread'}`} key={notification.id} onClick={() => void openNotification(notification)} disabled={markRead.isPending}><span className="notification-item__icon">{notification.relatedEntityType === 'PURCHASE_ORDER' ? <MessageSquare /> : <Bell />}</span><span><strong>{notification.subject}</strong><p>{notification.body}</p><time>{dateText(notification.createdAt)}</time></span>{!notification.isRead && <i />}</button>)}</div>
      {user?.userType !== 'VENDOR' && <footer><button onClick={() => { setOpen(false); navigate('/app/notifications'); }}>View all notifications</button></footer>}
    </section>}
  </div>;
}
